import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./CreateBlog.css"; // Custom styles for ReactQuill
import { toast } from "react-toastify";
import axios from "axios";

const CreateBlog = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [tags, setTags] = useState("");
  const [author, setAuthor] = useState({
    username: "",
    email: ""
  });
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages([...images, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !author.username.trim() || !author.email.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("content", content);
      // Change: Send author as a JSON string
      formData.append("author", JSON.stringify({
        username: author.username.trim(),
        email: author.email.trim()
      }));
      if (tags.trim()) {
        formData.append("tags", tags.trim());
      }

      // Append single image
      if (images.length > 0) {
        formData.append("image", images[0]);
      }

      const response = await axios.post(`${import.meta.env.VITE_APP_BASE_URL}/blogs`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true, // Include cookies for authentication
      });

      if (response.data.success) {
        toast.success("Blog created successfully and pending approval!");
        // Reset form
        setTitle("");
        setContent("");
        setImages([]);
        setTags("");
        setAuthor({
          username: "",
          email: ""
        });
      } else {
        toast.error(response.data.message || "Failed to create blog");
      }
    } catch (error) {
      console.error("Error creating blog:", error);
      const errorMessage = error.response?.data?.message || "Failed to create blog";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fcf9f5] to-white pb-16">
      <div className="relative h-[60vh] overflow-hidden flex items-center justify-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />

        {/* Red Overlay - Semi-transparent */}
        <div className="absolute inset-0 bg-[#7B0000] z-10"></div>

        {/* Content */}
        <div className="relative z-20 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-serif mb-4">Create New Blog</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
            Share your thoughts and insights with the world.
          </p>
          <div className="mt-8 inline-block">
            <div className="h-0.5 w-20 bg-white/70 mx-auto"></div>
            <div className="h-0.5 w-12 bg-white/50 mx-auto mt-1"></div>
            <div className="h-0.5 w-6 bg-white/30 mx-auto mt-1"></div>
          </div>
        </div>

        {/* Decorative Om Symbol */}
        <div className="absolute bottom-4 right-4 text-white/20 text-6xl font-serif z-10">ॐ</div>
      </div>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-[#B82A1E]/10 p-6 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Author Information - Add this section before the title */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-[#5D4037] mb-2">
                  Author Name *
                </label>
                <input
                  type="text"
                  id="username"
                  value={author.username}
                  onChange={(e) => setAuthor({ ...author, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#B82A1E] focus:border-[#B82A1E] transition"
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#5D4037] mb-2">
                  Author Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={author.email}
                  onChange={(e) => setAuthor({ ...author, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#B82A1E] focus:border-[#B82A1E] transition"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Title Input */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-[#5D4037] mb-2">
                Blog Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#B82A1E] focus:border-[#B82A1E] transition"
                placeholder="Enter your blog title"
                required
              />
            </div>

            {/* Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-[#5D4037] mb-2">
                Blog Content *
              </label>
              <div className="bg-white rounded-md border border-gray-300 quill-container">
                <ReactQuill
                  value={content}
                  onChange={setContent}
                  modules={modules}
                  theme="snow"
                  placeholder="Write your blog content here..."
                />
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label htmlFor="image" className="block text-sm font-medium text-[#5D4037] mb-2">
                Upload Image
              </label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={(e) => setImages(e.target.files ? Array.from(e.target.files) : [])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#B82A1E]/10 file:text-[#B82A1E] hover:file:bg-[#B82A1E]/20 cursor-pointer"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload a single image. Supported formats: JPG, PNG, GIF, etc.
              </p>
            </div>

            {/* Tags Input */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-[#5D4037] mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#B82A1E] focus:border-[#B82A1E] transition"
                placeholder="e.g., spirituality, meditation, wellness"
              />
            </div>

            {/* Image Preview */}
            {images.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[#5D4037] mb-2">Image Preview</h3>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-block bg-[#B82A1E] text-white font-medium px-8 py-3 rounded-full hover:bg-[#8a1f16] transition-colors duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting..." : "Submit Blog for Approval"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBlog;
